import { useState, type KeyboardEvent } from 'react';

import { useContactFieldSuggestions } from '@/api/queries/contactFieldSuggestions';
import { withForm } from '@/components/form';
import { Icon, IconButton, Input, SectionCard, Tag } from '@/components/ui';

import { defaultFormValues } from '../../contactSchema';

export const TagsSection = withForm({
  defaultValues: defaultFormValues,
  render: function TagsSection({ form }) {
    // Tenant-curated suggestions from the contact.tags counter table; free text stays allowed.
    const { suggestions } = useContactFieldSuggestions('tags');
    return (
      <SectionCard title="Tags & sources">
        <form.AppField name="tags">
          {(field) => {
            const tags = field.state.value ?? [];
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <SubLabel>Tags</SubLabel>
                  <div className="ds-tag-stack" style={{ flexWrap: 'wrap', gap: 5 }}>
                    {tags.length === 0 && (
                      <span style={{ color: 'var(--text-4)', fontSize: 12.5, fontStyle: 'italic' }}>
                        No tags
                      </span>
                    )}
                    {tags.map((t) => (
                      <span key={t} className="ds-tag brand" style={{ height: 22, paddingRight: 4 }}>
                        {t}
                        <IconButton
                          icon="x"
                          iconSize={10}
                          size="sm"
                          label={`Remove ${t}`}
                          style={{ width: 16, height: 16, marginLeft: 4, color: 'currentColor' }}
                          onClick={() =>
                            field.handleChange(tags.filter((x: string) => x !== t))
                          }
                        />
                      </span>
                    ))}
                    <TagAdder
                      suggestions={suggestions.filter((s) => !tags.includes(s))}
                      onAdd={(value) => {
                        if (!value) return;
                        if (tags.includes(value)) return;
                        field.handleChange([...tags, value]);
                      }}
                    />
                  </div>
                </div>

                <div>
                  <SubLabel>Sources</SubLabel>
                  <div className="ds-tag-stack" style={{ flexWrap: 'wrap', gap: 5 }}>
                    {/* Sources are owned by ingestion pipelines and not user-editable */}
                    <SourceList />
                  </div>
                </div>
              </div>
            );
          }}
        </form.AppField>
      </SectionCard>
    );
  },
});

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: 'var(--text-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 6,
        fontWeight: 600,
      }}
    >
      {children}
    </div>
  );
}

function TagAdder({
  suggestions,
  onAdd,
}: {
  suggestions: string[];
  onAdd: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const commit = () => {
    onAdd(draft.trim());
    setDraft('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        className="chip-btn"
        style={{ height: 22, fontSize: 11, padding: '0 6px' }}
        onClick={() => setOpen(true)}
      >
        <Icon name="plus" size={10} />
        Add tag
      </button>
    );
  }
  return (
    <>
    <Input
      autoFocus
      list={suggestions.length ? 'tag-suggestions' : undefined}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft.trim()) commit();
        else setOpen(false);
      }}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          setDraft('');
          setOpen(false);
        }
      }}
      placeholder="Tag name…"
      style={{ width: 140, height: 22, fontSize: 11, padding: '0 6px' }}
    />
    {suggestions.length > 0 && (
      <datalist id="tag-suggestions">
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    )}
    </>
  );
}

function SourceList() {
  // Sources aren't exposed through the form schema (they're appended by
  // ingestion pipelines on Contact.sources). The right-hand sidebar's
  // "Sources" card renders the live list — this stub just signals that the
  // section is intentionally read-only here.
  return (
    <Tag>
      <Icon name="link" size={10} style={{ marginRight: 4 }} />
      Managed by ingestion pipelines · see sidebar
    </Tag>
  );
}
